import type { UIMessage } from "@ai-sdk/react";
import type { ChatStreamMessageMetadata } from "@qw/qw-common";
import type { MaterialSymbol } from "@qw/ui";
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import type { ReactNode } from "react";

/**
 * Persisted `chat_message.status` values the client understands. The DB column
 * is free-form, so the wire type stays open (`string`), but comparisons in the
 * renderer/helpers should use these constants rather than raw literals so the
 * set of statuses that drive UI lives in one place.
 */
export const ChatMessageStatus = {
  Complete: "complete",
  Aborted: "aborted",
  Error: "error",
  Interrupted: "interrupted",
} as const;

export type ChatMessageStatus = (typeof ChatMessageStatus)[keyof typeof ChatMessageStatus];

export interface ExtendedUIMessage extends UIMessage {
  // Known values live in `ChatMessageStatus`; kept open because the server column
  // is free-form and older rows may carry values the client does not branch on.
  status?: ChatMessageStatus | (string & {}) | null;
  // Stable per-session shared message id (brief 011). Carried alongside `id` (the
  // DB PK, which stays the render identity) so the server-authoritative render path
  // can match a live turn's stream id against its persisted row. Additive only —
  // do NOT use this as the React key or `UIMessage.id`; it is session-unique, not
  // globally unique, so it must never become render identity on merged surfaces.
  externalMessageId?: string | null;
  // Globally-unique render identity — `base64url([sourceSessionId, externalMessageId])`
  // (brief 014 / B-1445). Persisted rows carry the server-computed value; a live streamed
  // turn derives the same value from its `sourceSessionId` metadata + stream id, so the
  // in-flight turn and its persisted row are ONE keyed element and reconcile in place at
  // settlement (no remount/blank). Prefer `getRenderKey` over reading this directly — it
  // falls back to `id` for turns with no derivable renderKey (legacy path, un-tagged turns).
  renderKey?: string | null;
  // The server-streamed chat metadata contract (B-1445/B-1454): `sourceSessionId` (renderKey
  // derivation) and `streamPartOffset` (accretion-prefix slice). Narrowed from `UIMessage`'s
  // `unknown` metadata and typed `Partial` because a turn may carry neither (user turns, legacy
  // rows) or only some fields mid-derivation — read defensively (`metadata?.streamPartOffset`).
  metadata?: Partial<ChatStreamMessageMetadata>;
}

export type MessagePart = NonNullable<UIMessage["parts"]>[number];

export type MessagePartToolInvocation = ToolUIPart | DynamicToolUIPart;

export interface SuggestionSegment {
  text: string;
}

export interface SuggestionInsertSlot {
  onInsert: (text: string) => void;
  extract?: (messagePart: MessagePart) => SuggestionSegment[];
}
// Adding a field here means updating `isSameSuggestionInsert` in `chat-session-messages.tsx`, which
// compares these fields to decide whether a settled message may keep its memo.

export interface McpResourceLink {
  type: "resource_link";
  name: string;
  title?: string;
  uri: string;
  description?: string;
  mimeType?: string;
  _meta?: Record<string, unknown>;
  [x: string]: unknown;
}

export interface ResourceLinkHandler {
  icon: MaterialSymbol;
  onClick: (resourceLink: McpResourceLink) => void | Promise<void>;
  tooltip?: (resourceLink: McpResourceLink) => string | ReactNode;
  /** Called when the resource link is first seen during streaming */
  onFirstSeen?: (resourceLink: McpResourceLink) => void | Promise<void>;
}

export interface FileResourceLinkHandlerContext {
  chatSessionId?: string;
  defaultAction: () => void | Promise<void>;
}

export interface FileResourceLinkHandler {
  onClick: (resourceLink: McpResourceLink, context: FileResourceLinkHandlerContext) => void | Promise<void>;
}
